import { useEffect, useState } from "react";
import { CheckCircle2, ClipboardList, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { solicitarPresupuesto } from "@/lib/api";

const VACIO = {
  nombre: "",
  telefono: "",
  email: "",
  tipo_celebracion: "",
  fecha: "",
  numero_personas: "",
  productos_solicitados: "",
  mensaje: "",
};

const TIPOS = [
  "Boda",
  "Comunión",
  "Bautizo",
  "Cumpleaños",
  "Aniversario",
  "Empresa",
  "Otra celebración",
];

export const PresupuestoModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(VACIO);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      setForm(VACIO);
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const hoy = new Date().toISOString().split("T")[0];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.tipo_celebracion) {
      setStatus("error");
      setErrorMsg("Selecciona el tipo de celebración.");
      return;
    }
    const personas = parseInt(form.numero_personas, 10);
    if (!personas || personas < 1) {
      setStatus("error");
      setErrorMsg("Indica el número de personas (mínimo 1).");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      await solicitarPresupuesto({ ...form, numero_personas: personas });
      setStatus("success");
      toast.success("Solicitud enviada. Te prepararemos el presupuesto lo antes posible.");
    } catch {
      setStatus("error");
      setErrorMsg("No se pudo enviar la solicitud. Revisa los campos e inténtalo de nuevo.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="presupuesto-modal"
        className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-lg"
      >
        {status === "success" ? (
          <div
            data-testid="presupuesto-modal-success"
            className="flex flex-col items-center py-8 text-center"
          >
            <CheckCircle2 className="h-14 w-14 text-green-700" strokeWidth={1.4} />
            <h3 className="mt-6 font-serif text-2xl text-tinta">Solicitud enviada</h3>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-tinta/65">
              Gracias, {form.nombre.split(" ")[0] || "gracias"}. Hemos recibido tu
              solicitud y te prepararemos el presupuesto lo antes posible.
            </p>
            <Button
              data-testid="presupuesto-modal-success-close"
              onClick={() => onOpenChange(false)}
              className="mt-8 rounded-full px-8"
            >
              Cerrar
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-tinta">
                Solicitar presupuesto
              </DialogTitle>
              <DialogDescription className="text-sm text-tinta/60">
                Cuéntanos cómo será tu celebración y te prepararemos una propuesta a
                medida.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="mt-5 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="pm-nombre">Nombre</Label>
                  <Input
                    id="pm-nombre"
                    data-testid="presupuesto-modal-nombre"
                    value={form.nombre}
                    onChange={set("nombre")}
                    placeholder="Tu nombre"
                    required
                    minLength={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pm-telefono">Teléfono</Label>
                  <Input
                    id="pm-telefono"
                    data-testid="presupuesto-modal-telefono"
                    type="tel"
                    value={form.telefono}
                    onChange={set("telefono")}
                    placeholder="+34 600 000 000"
                    required
                    minLength={6}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-email">Email</Label>
                <Input
                  id="pm-email"
                  data-testid="presupuesto-modal-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="tu@email.com"
                  required
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Tipo de celebración</Label>
                  <Select
                    value={form.tipo_celebracion}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, tipo_celebracion: v }))
                    }
                  >
                    <SelectTrigger data-testid="presupuesto-modal-tipo">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pm-fecha">Fecha</Label>
                  <Input
                    id="pm-fecha"
                    data-testid="presupuesto-modal-fecha"
                    type="date"
                    min={hoy}
                    value={form.fecha}
                    onChange={set("fecha")}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-personas">Número de personas</Label>
                <Input
                  id="pm-personas"
                  data-testid="presupuesto-modal-personas"
                  type="number"
                  min="1"
                  step="1"
                  value={form.numero_personas}
                  onChange={set("numero_personas")}
                  placeholder="Ej.: 40"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-productos">Productos solicitados</Label>
                <Input
                  id="pm-productos"
                  data-testid="presupuesto-modal-productos"
                  value={form.productos_solicitados}
                  onChange={set("productos_solicitados")}
                  placeholder="Ej.: tarta de boda, pastila marroquí, mini salados…"
                  required
                  minLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-mensaje">Mensaje (opcional)</Label>
                <Textarea
                  id="pm-mensaje"
                  data-testid="presupuesto-modal-mensaje"
                  value={form.mensaje}
                  onChange={set("mensaje")}
                  placeholder="Cuéntanos cualquier detalle que debamos saber"
                  rows={3}
                />
              </div>
              {status === "error" && (
                <p
                  data-testid="presupuesto-modal-error"
                  role="alert"
                  className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive"
                >
                  {errorMsg}
                </p>
              )}
              <Button
                type="submit"
                data-testid="presupuesto-modal-submit"
                disabled={status === "sending"}
                className="w-full rounded-full py-6 text-base"
              >
                {status === "sending" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ClipboardList className="h-4 w-4" />
                )}
                Solicitar presupuesto
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PresupuestoModal;
